import type { StateCreator } from "zustand";

import { setupWorkerMessageHandler } from "../../main-canvas/messages/handle-track-messages.ts";
import { sendSyntenyRenderMessage } from "../../main-canvas/messages/post-alignment-messages.ts";
import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../../main-canvas/render-main.ts";
import { TrackEnum, TrackTextEnum } from "../../openapi";
import { type SubType, type TrackConfiguration, type TrackGroups, type TrackType } from "../../types.ts";
import { getColorByTrackType } from "../../utils/color-conversion.ts";
import type { FetchError } from "../../utils/errors.ts";
import { createNewTracks, type OtherTrackConfig, type TrackConfig } from "../../utils/tracks/create-tracks.ts";
import { removeTracks } from "../../utils/tracks/remove-tracks.ts";
import type { GenomeViewerStore } from "../store.ts";

export interface TracksConfigurationState {
  currentlyAlignedTrackGroupIds: string[] | undefined;
}

export interface TracksConfigurationActions {
  getTrackConfiguration: () => TrackConfiguration;
  applyTrackConfiguration: (
    currentTrackConfiguration: TrackConfiguration,
    newTrackConfiguration: TrackConfiguration,
    onError: (error: Error | FetchError) => void,
  ) => void;
  changeTrackGroupPosition: (trackGroups: TrackGroups) => void;
  reorderTrackGroupsById: (orderedTrackGroupIds: string[]) => void;
  reorderTracksInGroup: (groupId: string, trackIds: string[]) => void;
}

export type TracksConfigurationSlice = TracksConfigurationState & TracksConfigurationActions;

export const initialTracksConfigurationState = {
  currentlyAlignedTrackGroupIds: undefined,
};

export const createTrackConfigurationSlice: StateCreator<GenomeViewerStore, [], [], TracksConfigurationSlice> = (
  set,
  get,
) => ({
  ...initialTracksConfigurationState,
  getTrackConfiguration() {
    const trackConfiguration: TrackConfiguration = new Map();
    // Map in the trackGroups
    for (const [groupId, trackGroup] of get().trackGroups) {
      const defaultTrackTypes: TrackType[] = [];
      const nuFeatureTypes: SubType[] = [];
      // Get the list of all track types within this trackGroup
      for (const trackId of trackGroup.trackIds) {
        const track = get().tracks.get(trackId);
        if (track) {
          if (track.trackType === TrackEnum.NUCLEOTIDE_FEATURE && track.subType) {
            nuFeatureTypes.push(track.subType);
          } else {
            defaultTrackTypes.push(track.trackType);
          }
        }
      }
      trackConfiguration.set(groupId, {
        defaultTypes: defaultTrackTypes,
        nuFeatureTypes: nuFeatureTypes,
      });
    }
    return trackConfiguration;
  },
  applyTrackConfiguration(currentTrackConfiguration, newTrackConfiguration, onError) {
    const { tracks: originalTracks, trackGroups: originalTrackGroups, organisms, alignedFamily, syntenyWorker } = get();

    const trackGroups = new Map(originalTrackGroups);
    const tracks = new Map(originalTracks);
    const newTrackConfigs: TrackConfig[] = [];
    const trackDeletions = [];

    // Map in the new track configuration
    for (const [groupId, trackFeatures] of newTrackConfiguration) {
      // Compute the added/removed tracks
      const added = trackFeatures.defaultTypes.filter(
        (trackType) => !currentTrackConfiguration.get(groupId)?.defaultTypes.includes(trackType),
      );
      const removed = currentTrackConfiguration
        .get(groupId)!
        .defaultTypes.filter((trackType) => !trackFeatures.defaultTypes.includes(trackType));

      // Compute the added/removed tracks of nuFeatures
      const addedNuFeatureTypes = trackFeatures.nuFeatureTypes.filter(
        (featureType) =>
          !currentTrackConfiguration.get(groupId)?.nuFeatureTypes.some((type) => type.id === featureType.id),
      );

      const removedNuFeatureTypes = currentTrackConfiguration
        .get(groupId)!
        .nuFeatureTypes.filter(
          (featureType) => !trackFeatures.nuFeatureTypes.some((type) => type.id === featureType.id),
        );

      // Load the added tracks
      for (const trackType of added) {
        newTrackConfigs.push({ trackType, group: groupId } as OtherTrackConfig);
      }

      // Load the added tracks of nuFeatures
      for (const featureType of addedNuFeatureTypes) {
        newTrackConfigs.push({
          trackType: TrackEnum.NUCLEOTIDE_FEATURE,
          group: groupId,
          options: {
            subType: featureType,
            color: getColorByTrackType(TrackEnum.NUCLEOTIDE_FEATURE),
            text: TrackTextEnum.INNER,
            isVisible: true,
          },
        });
      }

      // "Unmount" and clean the removed tracks
      for (const trackType of removed) {
        const track = [...tracks.values()].find((track) => track.trackType === trackType && track.groupId === groupId);
        if (track) {
          trackDeletions.push({ trackId: track.trackId, groupId: track.groupId });
        }
      }

      // "Unmount" and clean the removed tracks of nuFeatures
      for (const featureType of removedNuFeatureTypes) {
        const track = [...tracks.values()].find(
          (track) =>
            track.trackType === TrackEnum.NUCLEOTIDE_FEATURE &&
            track.subType?.id === featureType.id &&
            track.groupId === groupId,
        );
        if (track) {
          trackDeletions.push({ trackId: track.trackId, groupId: track.groupId });
        }
      }
    }

    const removeUpdate = removeTracks(trackDeletions, { trackGroups, tracks, alignedFamily });
    const addUpdate = createNewTracks(
      newTrackConfigs,
      {
        organisms,
        trackGroups: removeUpdate.trackGroups,
        tracks: removeUpdate.tracks,
      },
      (w) => setupWorkerMessageHandler(w, set, get, onError),
    );

    set(addUpdate);
    sendSyntenyRenderMessage(syntenyWorker);
    refreshTracks();
    // sendSyntenyRenderMessage();
  },
  changeTrackGroupPosition(trackGroups: TrackGroups) {
    set({ trackGroups });
    refreshMain();
    sendSyntenyRenderMessage(get().syntenyWorker);
  },
  reorderTrackGroupsById(orderedTrackGroupIds: string[]) {
    const trackGroupsLookup = get().trackGroups;

    const trackGroups: TrackGroups = new Map(
      orderedTrackGroupIds
        .filter((id) => trackGroupsLookup.has(id))
        .map((id) => {
          const trackGroup = trackGroupsLookup.get(id)!;
          return [id, trackGroup];
        }),
    );

    set({ trackGroups });
    // no need to render as already done when pressing apply in the track config dialog
  },
  reorderTracksInGroup: (groupId, trackIds) => {
    const trackGroups = get().trackGroups;
    const trackGroup = trackGroups.get(groupId);
    const syntenyWorker = get().syntenyWorker;

    if (trackGroup) {
      trackGroup.trackIds = trackIds;
    }

    set({
      trackGroups: new Map(trackGroups),
    });

    refreshMain();
    sendSyntenyRenderMessage(syntenyWorker);
  },
});
